import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

interface NotificationTargetSelectorProps {
  organizationId: string;
  targetType: string;
  setTargetType: (type: string) => void;
  targetId: string;
  setTargetId: (id: string) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

export const NotificationTargetSelector: React.FC<NotificationTargetSelectorProps> = ({
  organizationId,
  targetType,
  setTargetType,
  targetId,
  setTargetId,
}) => {
  const [classes, setClasses] = useState<SelectOption[]>([]);
  const [seasons, setSeasons] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Role options
  const roles = [
    { value: "owner", label: "Owners" },
    { value: "admin", label: "Administrators" },
    { value: "staff", label: "Staff Members" },
    { value: "parent", label: "Parents" },
  ];

  useEffect(() => {
    if (organizationId) {
      fetchTargetOptions();
    }
  }, [organizationId]);

  // Reset targetId when targetType changes
  useEffect(() => {
    setTargetId("");
  }, [targetType, setTargetId]);

  const fetchTargetOptions = async () => {
    setLoading(true);

    try {
      // Fetch classes
      const { data: classesData } = await supabase
        .from("classes")
        .select("id, name")
        .eq("organization_id", organizationId)
        .order("name");

      if (classesData) {
        setClasses(
          classesData.map((c) => ({
            value: c.id,
            label: c.name,
          }))
        );
      }

      // Fetch seasons
      const { data: seasonsData } = await supabase
        .from("seasons")
        .select("id, name")
        .eq("organization_id", organizationId)
        .order("name");

      if (seasonsData) {
        setSeasons(
          seasonsData.map((s) => ({
            value: s.id,
            label: s.name,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching target options:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <RadioGroup
          value={targetType}
          onValueChange={setTargetType}
          className="space-y-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="all" id="all" />
            <Label htmlFor="all" className="cursor-pointer">
              All Users
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <RadioGroupItem value="role" id="role" />
            <Label htmlFor="role" className="cursor-pointer">
              By Role
            </Label>
          </div>
          {targetType === "role" && (
            <div className="ml-6">
              <Select
                value={targetId}
                onValueChange={setTargetId}
                disabled={loading}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <RadioGroupItem value="class" id="class" />
            <Label htmlFor="class" className="cursor-pointer">
              By Class
            </Label>
          </div>
          {targetType === "class" && (
            <div className="ml-6">
              <Select
                value={targetId}
                onValueChange={setTargetId}
                disabled={loading || classes.length === 0}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a class" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map((cls) => (
                    <SelectItem key={cls.value} value={cls.value}>
                      {cls.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {classes.length === 0 && !loading && (
                <p className="text-sm text-muted-foreground mt-2">
                  No classes available
                </p>
              )}
            </div>
          )}

          <div className="flex items-center space-x-2">
            <RadioGroupItem value="season" id="season" />
            <Label htmlFor="season" className="cursor-pointer">
              By Season
            </Label>
          </div>
          {targetType === "season" && (
            <div className="ml-6">
              <Select
                value={targetId}
                onValueChange={setTargetId}
                disabled={loading || seasons.length === 0}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a season" />
                </SelectTrigger>
                <SelectContent>
                  {seasons.map((season) => (
                    <SelectItem key={season.value} value={season.value}>
                      {season.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {seasons.length === 0 && !loading && (
                <p className="text-sm text-muted-foreground mt-2">
                  No seasons available
                </p>
              )}
            </div>
          )}
        </RadioGroup>
      </CardContent>
    </Card>
  );
}; 